import React, { FunctionComponent, useEffect, useState } from "react"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import { IconContext } from "react-icons/lib"
import { FaCheck } from "react-icons/fa6"
import { TbUserScan } from "react-icons/tb"
import { ColumnHeaderCell, ColumnRowCell } from "@murphy-frontend/web-core/components/Table/Models"
import Table from "@murphy-frontend/web-core/components/Table"
import { useUserPreferences } from "../../../common/contexts/UserPreferencesContext"
import { useCustomer } from "../../../common/contexts/CustomerContext"
import { useUser } from "../../../common/contexts/UserContext"
import { useInjection } from "@murphy-frontend/common/contexts/InversifyContext"
import AuthServiceType, { IAuthService } from "@murphy-frontend/common/interfaces/IAuthService"
import Button from "@murphy-frontend/web-core/components/Button"
import TabPanel, { TabType } from "@murphy-frontend/web-core/components/Tabs/TabPanel"
import ComponentWrapper from "../../../common/components/ComponentWrapper"
import EditUserForm from "../components/EditUserForm"
import { useGetUser } from "@murphy-frontend/common/api/Users/Queries"
import { UserEducationColumns } from "@murphy-frontend/web-core/columns"
import { convertLevels, convertTrainingStates } from "@murphy-frontend/common/utils"
import FilterTag from "@murphy-frontend/web-core/components/FilterTag"
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext"
import { ModalConfiguration } from "@murphy-frontend/web-core/components/Modal"
import ResetConfirmationBody from "../components/ResetConfirmationBody"

const Me: FunctionComponent = () => {
	const [selectedTabId, setSelectedTabId] = useState<number>(0)
	const [educationRows, setEducationRows] = useState<ColumnRowCell[][]>([])
	const [educationColumns, setEducationColumns] = useState<ColumnHeaderCell[]>([])

	const { openModal, closeModal } = useModal()

	const { customer, allCustomerProducts } = useCustomer()
	const { user } = useUser()
	const { translations } = useUserPreferences()
	const authService = useInjection<IAuthService>(AuthServiceType.IAuthService)

	const {
		isLoading: isLoadingUser,
		isError: isErrorUser,
		data: dataUser,
		error: errorUser
	} = useGetUser(user.CustomerUserId)

	const onClickChangePassword = () => {
		authService.changePassword()
	}

	const onClickTab = (tabId: number) => {
		setSelectedTabId(tabId)
	}

	const levelTag = (level: number) => {
		const field = convertLevels(level, translations)

		return <FilterTag filterName={field} level={level} />
	}

	const onClickResetMFA = () => {
		const { Username, CustomerUserId } = dataUser
		const modalConf: ModalConfiguration = {
			body: (
				<ResetConfirmationBody
					userName={Username}
					customerUserId={CustomerUserId}
					closeModal={closeModal}
					translations={translations}
				/>
			),
			hideButton: true,
			type: "warning",
			position: "center",
			hideCloseIcon: true
		}

		openModal(modalConf)
	}

	useEffect(() => {
		if (translations) {
			setEducationColumns(UserEducationColumns(translations))
		}
	}, [translations])

	useEffect(() => {
		if (isLoadingUser) return

		if (dataUser.UserTrainingSessions) {
			const education = dataUser.UserTrainingSessions.filter(_ => ![2, 4].includes(_.TrainingSessionStateID)).map(
				(training, idx) => {
					return [
						{
							id: training.TrainingId,
							rowId: idx,
							displayValue: training.TrainingName,
							value: training.TrainingName,
							columnKey: "name"
						},
						{
							id: training.TrainingId,
							rowId: idx,
							displayValue: training.Levels ? levelTag(training.Levels) : "",
							// translated for sorting
							value: training.Levels ? convertLevels(training.Levels, translations) : "Z",
							customClass: "text-center",
							columnKey: "level"
						},
						{
							id: training.TrainingId,
							rowId: idx,
							displayValue: training.IsMandatory ? (
								<IconContext.Provider value={{ className: "btn-icon" }}>
									<FaCheck />
								</IconContext.Provider>
							) : (
								""
							),
							value: training.IsMandatory,
							columnKey: "mandatory"
						},
						{
							id: training.TrainingId,
							rowId: idx,
							displayValue: convertTrainingStates(training.TrainingSessionStateID, translations),
							value: convertTrainingStates(training.TrainingSessionStateID, translations),
							columnKey: "status"
						}
					]
				}
			)

			setEducationRows(education)
		}
	}, [dataUser, isLoadingUser])

	const initialTabs: TabType[] = [
		{
			id: 0,
			displayText: "Profile",
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-default-panel-tab-selected wrap-text"
		},
		{
			id: 1,
			displayText: "Education",
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-default-panel-tab-selected wrap-text"
		}
	]

	if (!allCustomerProducts || !user || !customer || isLoadingUser) {
		return (
			<ComponentWrapper active={8} subMenuActive={1}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	}

	return (
		<ComponentWrapper active={8} subMenuActive={1}>
			<section className='header-btns'>
				<div className='header-back-btn'>
					<h2>{dataUser.Username}</h2>
				</div>
			</section>
			<section className='table-container-general'>
				<div className='user-tab-wrapper'>
					<TabPanel
						tabs={initialTabs}
						onClickTab={onClickTab}
						selectedTabId={selectedTabId}
						customClass='tab-solid-panel-tms'
					/>
				</div>
				{selectedTabId === 1 && <Table rows={educationRows} columns={educationColumns} key={"education"} />}
			</section>
			{selectedTabId === 0 && (
				<section className='card-container edit-card-container'>
					<EditUserForm
						translations={translations}
						userData={dataUser}
						products={allCustomerProducts}
						disabled={false}
						isUserProfile={true}
					/>
				</section>
			)}
			{selectedTabId === 0 && (
				<section className='edit-user-btns'>
					<Button buttonSize={"btn--medium"} isLightButton={true} onClick={onClickResetMFA}>
						<IconContext.Provider value={{ className: "btn-icon useradmin-more" }}>
							<TbUserScan />
						</IconContext.Provider>
						{translations.restMfa}
					</Button>
					<Button buttonSize={"btn--medium"} isLightButton={true} onClick={onClickChangePassword}>
						Change Password
					</Button>
				</section>
			)}
		</ComponentWrapper>
	)
}

export default Me
